/**
 * rowMutationsSlice — explicit row-mutation undo log (TV1-1690 / B-6).
 *
 * Why this exists
 * ---------------
 * Previously `rowsReducer` was wrapped in `undoable({ limit: 50 })`. Every
 * `upsertRows`/`updateRows` of 5k rows pushed a full snapshot of the entity-
 * adapter state onto the undo stack — at scale this dominated heap usage
 * (50 snapshots × ~5k row clones ≈ 250k clones). Cell edits do NOT need a
 * full-rows snapshot to be reversible; the per-cell `cellChangesReducer`
 * already records pre/post values.
 *
 * Row-LEVEL mutations (insert / delete) DO need to be reversible, but their
 * deltas are O(delta-size), not O(all rows). We capture those deltas here.
 *
 * Design
 * ------
 * We skip `redux-undo` for this slice and roll a tiny past/future log. The
 * reasons:
 *   - We need to APPLY THE INVERSE on the `rows` slice when the user undoes
 *     a row mutation; redux-undo only restores THIS slice's state, which
 *     would leave the rows slice diverged.
 *   - The state we track here is the operation itself — restoring the past
 *     state of *this* slice is meaningless.
 *
 * So we expose:
 *   - `recordInsert` / `recordDelete` reducers — append a mutation to `past`,
 *     enforce limit, clear `future`. Dispatched IMMEDIATELY AFTER the row
 *     mutation itself.
 *   - `undoRowMutation()` / `redoRowMutation()` thunks — read the head of
 *     past/future, dispatch the inverse row action against `rowsSlice`, and
 *     move the entry between past/future.
 *   - `clearMutations` — wipe both logs (called alongside
 *     `ActionCreators.clearHistory()` in `SpreadsheetEditor` when rows are
 *     reloaded externally).
 *
 * The undo limit here is intentionally 10 — row-level mutations are rarer
 * than cell edits and each entry carries actual row payloads.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Row } from '../../types/Spreadsheet';
import { AppDispatch, RootState } from '../store';
import { addRows, insertRowsAtIndex, removeRows } from './rowsSlice';

const ROW_MUTATIONS_LIMIT = 10;

export type RowInsertMutation = {
  type: 'insert';
  atIndex: number;
  rows: Row[];
  mutationId: string;
};

export type RowDeleteMutation = {
  type: 'delete';
  rowIds: string[];
  previousRows: Row[];
  mutationId: string;
};

export type RowMutation = RowInsertMutation | RowDeleteMutation;

type RowMutationsState = {
  past: RowMutation[]; // mutations that have been applied (oldest -> newest)
  future: RowMutation[]; // mutations that have been undone (most-recently-undone first)
};

const initialState: RowMutationsState = { past: [], future: []};

const rowMutationsSlice = createSlice({
  name: 'rowMutations',
  initialState,
  reducers: {
    recordInsert( state, action: PayloadAction<Omit<RowInsertMutation, 'type'>> ) {
      state.past.push({ ...action.payload, type: 'insert' });
      if ( state.past.length > ROW_MUTATIONS_LIMIT ) {
        // Drop the oldest entry. Undo beyond the limit is intentionally lost —
        // matching the bounded-history contract of `redux-undo({ limit: 10 })`.
        state.past.shift();
      }
      state.future = [];
    },
    recordDelete( state, action: PayloadAction<Omit<RowDeleteMutation, 'type'>> ) {
      state.past.push({ ...action.payload, type: 'delete' });
      if ( state.past.length > ROW_MUTATIONS_LIMIT ) {
        state.past.shift();
      }
      state.future = [];
    },
    // Internal — moves the head of `past` to the head of `future`. The thunk
    // applies the inverse on `rows` BEFORE calling this so the two slices
    // stay coherent within a single dispatch chain.
    undoPop( state ) {
      const m = state.past.pop();
      if ( m ) state.future.unshift( m );
    },
    // Internal — moves the head of `future` back to the tail of `past`.
    redoPop( state ) {
      const m = state.future.shift();
      if ( m ) state.past.push( m );
    },
    clearMutations( state ) {
      state.past = [];
      state.future = [];
    }
  }
});

export const {
  recordInsert,
  recordDelete,
  undoPop,
  redoPop,
  clearMutations
} = rowMutationsSlice.actions;

export default rowMutationsSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectRowMutationsPast = ( state: RootState ): RowMutation[] =>
  state.spreadsheet.rowMutations.past;

export const selectRowMutationsFuture = ( state: RootState ): RowMutation[] =>
  state.spreadsheet.rowMutations.future;

export const selectCanUndoRowMutation = ( state: RootState ): boolean =>
  state.spreadsheet.rowMutations.past.length > 0;

export const selectCanRedoRowMutation = ( state: RootState ): boolean =>
  state.spreadsheet.rowMutations.future.length > 0;

// ---------------------------------------------------------------------------
// Thunks — apply the inverse on rowsSlice, then move the entry past <-> future
// ---------------------------------------------------------------------------

/**
 * Undo the most recent row mutation.
 *
 * Inverse rules:
 *   - insert(rows@atIndex) -> removeRows(rows.map(r => r.id))
 *   - delete(rowIds, previousRows) -> addRows(previousRows)
 *
 * Note on delete-undo positioning: rowsAdapter has no stable concept of
 * "restore at original position"; addRows appends. That mirrors what the
 * pre-refactor `undoable` wrapper effectively did once the entity adapter
 * re-laid the entities. If precise ordering becomes a regression we can
 * upgrade `delete` to capture per-row index and use `insertRowsAtIndex` here.
 */
export const undoRowMutation = () => ( dispatch: AppDispatch, getState: () => RootState ) => {
  const past = getState().spreadsheet.rowMutations.past;
  const last = past[past.length - 1];
  if ( !last ) return;

  if ( last.type === 'insert' ) {
    const rowIds = last.rows.map(( r ) => r.id );
    dispatch( removeRows( rowIds ));
  } else {
    dispatch( addRows( last.previousRows ));
  }
  dispatch( undoPop());
};

/**
 * Redo the most recently undone row mutation. Re-applies the original
 * operation (insert -> insertRowsAtIndex, delete -> removeRows).
 */
export const redoRowMutation = () => ( dispatch: AppDispatch, getState: () => RootState ) => {
  const future = getState().spreadsheet.rowMutations.future;
  const next = future[0];
  if ( !next ) return;

  if ( next.type === 'insert' ) {
    dispatch( insertRowsAtIndex({ index: next.atIndex, newRows: next.rows }));
  } else {
    const rowIds = next.previousRows.map(( r ) => r.id );
    dispatch( removeRows( rowIds ));
  }
  dispatch( redoPop());
};
